import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { FiArchive, FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import DataTable from "../common/DataTable";
import TablePagination from "../common/TablePagination";
import StatusBadge from "../common/StatusBadge";
import useAdminResource from "../../hooks/useAdminResource";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import adminUserService from "../../services/adminUserService";
import { formatWhen, formatDate, formatTime } from "./scheduleFormat";
import { ScheduleDevices } from "./ScheduleBits";
import { ScheduleDetailModal } from "./DetailModals";

/**
 * Archived schedules — the admin counterpart of the app's ArchivedSchedules
 * screen ("Completed + Archived").
 *
 * Two things make this its own tab rather than a filter on Schedules:
 *  - it is a "recently removed" list, so it orders by archivedAt, not nextRunAt
 *  - the columns differ: what the schedule WAS before archiving and when it was
 *    archived matter here and are noise everywhere else
 *
 * ⚠️ Read-only, so there is no Restore or Delete. The app has both; admin
 * deliberately does not — restoring someone's schedule would put it back into
 * execution, and restore re-pins it to `pause` for a reason.
 *
 * ⚠️ A finished one-time schedule appears here too: `once` finalises to
 * cronstatus "completed" + status "archived". That is not a user action, so the
 * Run state column is what distinguishes it from a manually archived one.
 */
const SORTS = [
  ["archivedAt", "Recently archived"],
  ["name", "Name"],
];

export default function UserArchiveTab() {
  const { userId, deviceIndex } = useOutletContext();
  const navigate = useNavigate();
  const basePath = `/admin/dash/users/${userId}`;

  const DEFAULTS = { search: "", sortBy: "archivedAt" };
  const [searchParams, setSearchParams] = useSearchParams();
  const get = (k) => searchParams.get(k) ?? DEFAULTS[k];

  const search = get("search");
  const sortBy = get("sortBy");
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);

  const debouncedSearch = useDebouncedValue(search, 300);
  const [detailSchedule, setDetailSchedule] = useState(null);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          const isDefault =
            v === undefined || v === null || (k in DEFAULTS && String(v) === DEFAULTS[k]);
          if (isDefault) next.delete(k);
          else next.set(k, String(v));
        }
        if (!("page" in patch)) next.delete("page");
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  const fetcher = useCallback(
    () =>
      adminUserService.fetchUserSchedules(userId, {
        page,
        limit,
        search: debouncedSearch,
        status: "archived",
        sortBy,
        // Newest archive first; by name it reads better ascending.
        sortOrder: sortBy === "name" ? "asc" : "desc",
      }),
    [userId, page, limit, debouncedSearch, sortBy]
  );

  const { data, meta, state, error, refetch, dismissError } = useAdminResource(
    fetcher,
    [userId, page, limit, debouncedSearch, sortBy]
  );

  const schedules = data || [];

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Schedule",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="min-w-[180px]">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-700">{s.name}</span>
                {s.smartRestore && (
                  <span title="Smart Restore was on" className="text-indigo-400">✨</span>
                )}
                {s.signal === "red" && (
                  <FiAlertCircle size={14} className="text-red-400" title="Subscription expired" />
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-slate-400">{s.roomSummary}</p>
            </div>
          );
        },
      },
      {
        id: "archivedAt",
        header: "Archived",
        cell: ({ row }) => (
          <div className="text-sm text-slate-600">
            {formatDate(row.original.archivedAt)}
            <p className="text-[11px] text-slate-400">
              {formatTime(row.original.archivedAt)}
            </p>
          </div>
        ),
      },
      {
        id: "wasStatus",
        header: "Was",
        // What restore would put it back to. Without this an admin cannot tell
        // whether the schedule was running or already off when it was archived.
        cell: ({ row }) =>
          row.original.statusBeforeArchive ? (
            <StatusBadge kind="scheduleStatus" value={row.original.statusBeforeArchive} />
          ) : (
            <span className="text-sm text-slate-400">—</span>
          ),
      },
      {
        id: "cronstatus",
        header: "Run state",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div>
              <StatusBadge kind="cronStatus" value={s.cronstatus} />
              {s.cronstatus === "completed" && (
                <p className="mt-1 text-[11px] text-slate-400">ran &amp; finished</p>
              )}
            </div>
          );
        },
      },
      {
        id: "when",
        header: "When",
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{formatWhen(row.original)}</span>
        ),
      },
      {
        id: "devices",
        header: "Devices",
        cell: ({ row }) => (
          <ScheduleDevices
            schedule={row.original}
            deviceIndex={deviceIndex}
            basePath={basePath}
            navigate={navigate}
          />
        ),
      },
      {
        id: "detail",
        header: "",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDetailSchedule(row.original); }}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Details
          </button>
        ),
      },
    ],
    [deviceIndex, basePath, navigate]
  );

  const isFiltered = Object.keys(DEFAULTS).some(
    (k) => (searchParams.get(k) ?? DEFAULTS[k]) !== DEFAULTS[k]
  );

  const select =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => patchParams({ search: e.target.value })}
          placeholder="Search archived schedule…"
          className={`${select} min-w-[200px] flex-1`}
        />
        <select value={sortBy} onChange={(e) => patchParams({ sortBy: e.target.value })} className={select}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw size={14} className={state === "loading" ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <DataTable
        columns={columns}
        data={schedules}
        state={state}
        error={error}
        onRetry={() => refetch()}
        onDismissError={dismissError}
        getRowId={(row) => String(row.scheduleId)}
        onRowClick={(row) => setDetailSchedule(row)}
        emptyIcon={FiArchive}
        emptyTitle={isFiltered ? "No archived schedules match" : "Nothing archived"}
        emptyMessage={
          isFiltered
            ? "Try a different search."
            : "This user has not archived any schedules, and none have run to completion."
        }
        isFiltered={isFiltered}
        onClearFilters={() => setSearchParams(new URLSearchParams())}
      />

      <TablePagination
        page={meta.page}
        totalPages={meta.totalPages}
        total={meta.total}
        limit={meta.limit}
        onPageChange={(p) => patchParams({ page: p })}
        onLimitChange={(l) => patchParams({ limit: l, page: 1 })}
      />

      <ScheduleDetailModal
        open={Boolean(detailSchedule)}
        onClose={() => setDetailSchedule(null)}
        userId={userId}
        schedule={detailSchedule}
        deviceIndex={deviceIndex}
      />
    </div>
  );
}
